import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { contentService } from '../../../chancencampus/src/client/globals.js';
import { ScopedEntity } from '../../db/ScopedEntity.js';
import { LevumiMathCategory } from '../../db/enums/CampusLevumiEnums.js';
import { ExerciseType } from '../../db/enums/enums.js';
import { ContentResultEventlog } from '../../db/eventlogs/ContentResultEventlog.js';
import { CampusLevumiLevel } from './levumi/CampusLevumiLevel.js';

@Entity()
export class CampusExercise extends ScopedEntity {
  public static override _class = 'CampusExercise';
  public static override _collectionName = 'campus-exercises';

  static override get allFields() {
    return selectionSet(() => CampusExercise, 3, ['answers']);
  }

  static latexExpressionRegex = /\$(.*?)\$/g;

  @Field(type => Int, {
    default: v => v ?? ExerciseType.H5P,
  })
  public type!: number;

  @Field(type => String)
  public levumiLevelID: Maybe<string>;

  @DataLoadedField(type => CampusLevumiLevel, ({ levumiLevelID }) => levumiLevelID)
  public levumiLevel!: CampusLevumiLevel;

  @Field(type => String, { default: v => v ?? LevumiMathCategory.ADD })
  public levumiMathCategory!: LevumiMathCategory;

  @Field(type => String)
  public questionText: Maybe<string>;

  @Field(type => String)
  public questionAnswer: Maybe<string>;

  @Field(type => String)
  public questionAnswerVariableFields: Maybe<string>;

  @Field(type => String)
  public h5pContentID: Maybe<string>;

  @Field(type => String)
  public h5pContentDesc: Maybe<string>;

  @Field(type => String)
  public h5pContentTitle: Maybe<string>;

  @Field(type => Int, {
    default: v => v ?? -1,
  })
  public minAnswerLength!: number;

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  public isSubmittable!: boolean;

  @Field(type => String)
  public mediaID: Maybe<string>;

  @DataLoadedField(type => ImageFile, ({ mediaID }) => mediaID)
  public media!: ImageFile;

  @Field(type => Int, {
    default: v => v ?? 0,
  })
  public sortingOrderInLesson!: number;

  @Field(type => String)
  public title: Maybe<string>;

  @Field(type => String)
  public serloContentID: Maybe<string>;

  @Field(type => String, {
    default: v => v ?? '',
  })
  public serloEditorState: Maybe<string>;

  @Field(type => String)
  public chemistryFormula: Maybe<string>;

  @Field(type => GeogebraExercise, {
    default: v => v ?? new GeogebraExercise({}),
  })
  public geogebraContent!: GeogebraExercise;

  @Field(type => [ContentResultEventlog], {
    resolve: function (this: CampusExercise) {
      return async (gqlFields: BaseQLSelectionSet<ContentResultEventlog>) => this.answers;
    },
  })
  answers?: ContentResultEventlog[];

  public get typeString() {
    switch (this.type) {
      case ExerciseType.H5P:
        return 'H5P';
      case ExerciseType.FREITEXT:
        return 'Freitext';
      case ExerciseType.FORMEL:
        return 'Formel';
      case ExerciseType.GEOGEBRA:
        return 'GeoGebra';
      case ExerciseType.MEDIA:
        return 'Media-Aufgabe';
      case ExerciseType.LEVUMIGERMAN:
        return 'LEVUMI-Test Deutsch';
      case ExerciseType.LEVUMIMATH:
        return 'LEVUMI-Test Mathematik';
      case ExerciseType.SERLO:
        return 'Serlo';
      case ExerciseType.SERLO_EDITOR:
        return 'Serlo Editor';
      case ExerciseType.CHEMISTRY_FORMULA:
        return 'Chemie-Formel';
      default:
        return 'Unknown';
    }
  }

  public override async remove() {
    if (this.type === ExerciseType.H5P) {
      if (!this.h5pContentID) throw new Error('expecting h5pContentID on exercise of type h5p');
      await contentService.delete(this.h5pContentID);
    }
    if (this.type === ExerciseType.MEDIA && this.mediaID !== '') {
      await ImageFile.removeFile(this.mediaID ?? '')();
    }

    const lessonOfExercise = await LearningContentAPI.getLessonForExercise(this._id)({ exerciseIDs: 1 });
    if (!lessonOfExercise) throw new Error('Lesson of Exercise not found, this shouldnt happen');
    const indexOfRemovedExercise = lessonOfExercise.exerciseIDs.findIndex(lessonID => lessonID === this._id);
    lessonOfExercise.exerciseIDs.splice(indexOfRemovedExercise, 1);
    await lessonOfExercise.save();
    return super.remove();
  }
}
/**
 * @entity GeogebraExercise
 */
@Entity()
export class GeogebraExercise extends AdornisEntity {
  public static override _class = 'GeogebraExercise';

  @Field(type => String)
  public geogebraID!: string;

  @Field(type => String)
  public height!: string;

  @Field(type => String)
  public width!: string;
}
